import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  TableKit,
  FormDialog,
  FormCard,
  FormSection,
  FormGrid,
  FormField,
  FormSearchSelect,
  FormAlert,
  ConfirmDialog,
  SimpleTable,
  toHebrewNumeral,
} from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import { TRACTATE_NAMES, getMaxDaf } from '../../data/gemaraTractates.js';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import { useQuickCreate } from '../../next/quickCreate';

export const FRAMES = [
  { value: 'daf_yomi', label: 'דף היומי' },
  { value: 'shinun', label: 'דף השינון' },
  { value: 'meshannim', label: 'משננים' },
];

export const ALLOWED_FRAMES = new Set(FRAMES.map((f) => f.value));
export const FRAME_LABELS = Object.fromEntries(FRAMES.map((f) => [f.value, f.label]));

function RowActionsMenu({
  row,
  onEditExam,
  onDownloadWord,
  onEditDetails,
  onReplaceExcel,
  onToggleActive,
  onDelete,
}) {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => { e.stopPropagation(); onEditExam(row); }}
        sx={{ whiteSpace: 'nowrap', minWidth: 0, px: 1, fontSize: 12, height: 28 }}
      >
        עריכת מבחן
      </Button>
      <Button
        size="small"
        variant="outlined"
        disabled={!row.questions?.length}
        onClick={(e) => { e.stopPropagation(); onDownloadWord(row); }}
        sx={{ whiteSpace: 'nowrap', minWidth: 0, px: 1, fontSize: 12, height: 28 }}
      >
        הורדה כוורד
      </Button>
      <IconButton
        size="small"
        aria-label="פעולות נוספות"
        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{ width: 28, height: 28 }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={() => { setAnchor(null); onEditDetails(row); }}>
          <ListItemText>עריכת פרטים</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); onReplaceExcel(row); }}>
          <ListItemText>החלף אקסל</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); onToggleActive(row); }}>
          <ListItemText>{row.status === 'active' ? 'כבה' : 'הפעל'}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => { setAnchor(null); onDelete(row); }}
          sx={{ color: 'var(--ck-danger, #dc2626)' }}
        >
          <ListItemText>מחיקה</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
}

export const SECTION_LABELS = {
  main: 'ראשי',
  zchut: 'זכות',
  tosefet: 'תוספות',
  lechiduda: 'לחידודא',
  tzurta: 'צורתא דשמעתתא',
};

export const SECTION_ORDER = ['main', 'zchut', 'tosefet', 'lechiduda', 'tzurta'];

/** תוויות תצוגה כמו בייחוס: . = עמוד א, : = עמוד ב */
const AMUD_OPTIONS = [
  { value: 'a', label: '.' },
  { value: 'b', label: ':' },
];

export function normalizeSearch(s) {
  return String(s || '')
    .replace(/[\u05F3\u05F4"'`׳״]/g, '')
    .replace(/\s+/g, '')
    .toLowerCase();
}

function filterOptions(options, query, getOptionLabel) {
  const q = normalizeSearch(query);
  if (!q) return options;
  return options.filter((opt) => {
    const label = normalizeSearch(getOptionLabel(opt));
    const raw = normalizeSearch(opt);
    return label.includes(q) || raw.includes(q);
  });
}

/** התאמה מדויקת להקלדה (מספר או תווית עברית) — כדי שלא יישאר טקסט בלי ערך נבחר */
function findExactOption(options, query, getOptionLabel) {
  const q = normalizeSearch(query);
  if (!q) return null;
  const hits = options.filter((opt) => {
    const label = normalizeSearch(getOptionLabel(opt));
    const raw = normalizeSearch(opt);
    return label === q || raw === q;
  });
  return hits.length === 1 ? hits[0] : null;
}

/** FormSearchSelect לא מסנן פנימית — מעבירים options מסוננים לפי הקלדה */
function SearchSelect({
  options = [],
  getOptionLabel = (opt) => String(opt ?? ''),
  onChange,
  value,
  ...rest
}) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(
    () => filterOptions(options, query, getOptionLabel),
    [options, query, getOptionLabel]
  );
  return (
    <FormSearchSelect
      {...rest}
      value={value}
      options={filtered}
      getOptionLabel={getOptionLabel}
      onInputChange={(v) => {
        const next = v || '';
        setQuery(next);
        const exact = findExactOption(options, next, getOptionLabel);
        if (exact != null && !(value != null && (exact === value || Number(exact) === Number(value)))) {
          onChange?.(exact);
        }
      }}
      onChange={(v) => {
        setQuery('');
        onChange?.(v);
      }}
    />
  );
}

const dafOptionLabel = (n) => (n == null ? '' : toHebrewNumeral(n));

function DafAmudField({
  label,
  required,
  dafOptions,
  dafValue,
  onDafChange,
  dafPlaceholder,
  showAmud,
  amudValue,
  onAmudChange,
}) {
  return (
    <div className="formkit-field">
      {label && (
        <label className={`formkit-label${required ? ' formkit-label--required' : ''}`}>
          {label}
        </label>
      )}
      <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <SearchSelect
            compact
            options={dafOptions}
            value={dafValue}
            onChange={onDafChange}
            getOptionLabel={dafOptionLabel}
            isOptionEqual={(a, b) => Number(a) === Number(b)}
            placeholder={dafPlaceholder}
          />
        </div>
        {showAmud && (
          <select
            className="formkit-select"
            aria-label="עמוד"
            value={amudValue || 'a'}
            onChange={(e) => onAmudChange(e.target.value)}
            style={{ width: 56, flexShrink: 0 }}
          >
            {AMUD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

export const emptyForm = () => ({
  title: '',
  frame: 'daf_yomi',
  description: '',
  tractate: null,
  tractateTo: null,
  dafFrom: null,
  dafTo: null,
  amudFrom: 'a',
  amudTo: 'b',
  file: null,
});

export function buildDafOptions(maxDaf) {
  if (!maxDaf || maxDaf < 2) return [];
  const opts = [];
  for (let n = 2; n <= maxDaf; n += 1) opts.push(n);
  return opts;
}

export function clampDaf(daf, maxDaf) {
  if (daf == null || daf === '') return null;
  const n = Number(daf);
  if (!Number.isInteger(n) || n < 2) return null;
  if (maxDaf && n > maxDaf) return null;
  return n;
}

export function sectionCounts(questions = []) {
  const counts = Object.fromEntries(SECTION_ORDER.map((k) => [k, 0]));
  for (const q of questions) {
    if (counts[q.sectionKey] != null) counts[q.sectionKey] += 1;
  }
  return counts;
}

export function formatCountsSummary(questions = []) {
  const counts = sectionCounts(questions);
  return SECTION_ORDER
    .filter((k) => counts[k] > 0)
    .map((k) => `${SECTION_LABELS[k]} ${counts[k]}`)
    .join(' · ');
}

export function formatMaterial(material, frame) {
  if (!material?.tractate) return '';
  const { tractate, dafFrom, dafTo, amudFrom, amudTo } = material;
  const tractateTo = material.tractateTo || tractate;
  const fromHeb = toHebrewNumeral(dafFrom);
  const toHeb = toHebrewNumeral(dafTo);
  const amudHeb = { a: 'א', b: 'ב' };
  const cross = tractateTo !== tractate;

  if (frame === 'shinun' && amudFrom && amudTo) {
    const fromPart = `${tractate} דף ${fromHeb} עמוד ${amudHeb[amudFrom] || amudFrom}`;
    const toPart = cross
      ? `${tractateTo} דף ${toHeb} עמוד ${amudHeb[amudTo] || amudTo}`
      : `דף ${toHeb} עמוד ${amudHeb[amudTo] || amudTo}`;
    return `${fromPart} – ${toPart}`;
  }
  if (!cross && dafFrom === dafTo) return `${tractate} דף ${fromHeb}`;
  if (!cross) return `${tractate} דף ${fromHeb}–${toHeb}`;
  return `${tractate} דף ${fromHeb} – ${tractateTo} דף ${toHeb}`;
}

export function truncate(s, n = 40) {
  const t = String(s || '');
  return t.length > n ? `${t.slice(0, n)}…` : t;
}

export function questionTotal(exam) {
  const fromQs = (exam.questions || []).length;
  if (fromQs) return fromQs;
  return (exam.sections || []).reduce((s, sec) => s + (sec.questionCount || 0), 0);
}

/** כמו examFlow: פעיל + מוכן, מיון createdAt יורד → עד 2 לכל מסגרת */
function examReadyForIvr(exam) {
  if (exam.status !== 'active') return false;
  return questionTotal(exam) > 0;
}

export function buildIvrSlotMap(exams) {
  const byFrame = {};
  for (const exam of exams) {
    if (!examReadyForIvr(exam)) continue;
    const f = exam.frame || '';
    if (!byFrame[f]) byFrame[f] = [];
    byFrame[f].push(exam);
  }
  const slotById = {};
  for (const list of Object.values(byFrame)) {
    list.sort((a, b) => {
      // לפי מועד הפתיחה (במבחנים ישנים בלי תאריך — לפי יצירה), כמו בקו
      const ta = new Date(a.opensAt || a.createdAt || 0).getTime();
      const tb = new Date(b.opensAt || b.createdAt || 0).getTime();
      if (tb !== ta) return tb - ta;
      return (a.sortOrder || 0) - (b.sortOrder || 0);
    });
    list.slice(0, 2).forEach((exam, i) => {
      slotById[exam._id] = i + 1;
    });
  }
  return slotById;
}

export function formatCreatedAt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export const IVR_SLOT_LABELS = {
  1: '1 · השבוע',
  2: '2 · שבוע שעבר',
};

const MAPPING_COLUMNS = [
  { key: 'number', title: '#', align: 'center' },
  {
    key: 'sectionKey',
    title: 'סוג',
    render: (v) => SECTION_LABELS[v] || v,
  },
  {
    key: 'text',
    title: 'שאלה',
    render: (v) => (
      <span style={{ fontSize: '0.85em' }} title={v || ''}>
        {truncate(v, 60)}
      </span>
    ),
  },
  { key: 'correctOption', title: 'נכונה', align: 'center' },
  {
    key: 'options',
    title: 'אפשרויות',
    render: (opts) => (
      <span style={{ fontSize: '0.85em', color: 'var(--ck-text-2, #64748b)' }}>
        {(opts || []).map((opt, i) => (
          <span key={i} style={{ display: 'block' }}>{i + 1}. {truncate(opt)}</span>
        ))}
      </span>
    ),
  },
];

const AmericanExams = () => {
  useQuickCreate(() => openCreate());
  const { frame: frameParam } = useParams();
  const frame = String(frameParam || '').trim();
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const [replaceError, setReplaceError] = useState('');
  const [excelPreview, setExcelPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const replaceFileRef = useRef(null);
  const previewReqRef = useRef(0);

  const goToEditor = useCallback((examId) => {
    if (!examId || !frame) return;
    navigate(`/american-exams/${frame}/${examId}`);
  }, [navigate, frame]);

  const load = useCallback(async () => {
    if (!ALLOWED_FRAMES.has(frame)) return;
    setLoading(true);
    try {
      const { data } = await api.get('/american-exams', { params: { frame } });
      setExams(data.data || []);
    } catch (err) {
      console.error(err);
      setFlash({ severity: 'error', message: 'שגיאה בטעינת מבחנים' });
    } finally {
      setLoading(false);
    }
  }, [frame]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 6000);
    return () => clearTimeout(t);
  }, [flash]);

  const maxDafFrom = form.tractate ? getMaxDaf(form.tractate) : null;
  const tractateToEffective = form.tractateTo || form.tractate;
  const maxDafTo = tractateToEffective ? getMaxDaf(tractateToEffective) : null;
  const dafFromOptions = useMemo(() => buildDafOptions(maxDafFrom), [maxDafFrom]);
  const dafToOptions = useMemo(() => buildDafOptions(maxDafTo), [maxDafTo]);

  const handleTractateFromChange = (v) => {
    setForm((f) => {
      const syncTo = !f.tractateTo || f.tractateTo === f.tractate;
      const max = getMaxDaf(v);
      const next = {
        ...f,
        tractate: v,
        dafFrom: clampDaf(f.dafFrom, max),
      };
      if (syncTo) {
        next.tractateTo = v;
        next.dafTo = clampDaf(f.dafTo, max);
      }
      return next;
    });
  };

  const handleTractateToChange = (v) => {
    setForm((f) => {
      const max = getMaxDaf(v);
      return {
        ...f,
        tractateTo: v,
        dafTo: clampDaf(f.dafTo, max),
      };
    });
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const { data } = await api.get('/american-exams/template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_american_exam.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setFlash({ severity: 'error', message: 'שגיאה בהורדת התבנית' });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const downloadWord = async (exam) => {
    try {
      const { data } = await api.get(`/american-exams/${exam._id}/docx`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(exam.title || 'מבחן').replace(/[/\\?%*:|"<>]/g, '-')}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setFlash({ severity: 'error', message: 'שגיאה בהורדת קובץ הוורד' });
    }
  };

  const importFile = async (examId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post(`/american-exams/${examId}/import`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  const clearExcelPreview = () => {
    previewReqRef.current += 1;
    setExcelPreview(null);
    setPreviewLoading(false);
    setPreviewError('');
  };

  const formatPreviewApiError = (err) => {
    const payload = err.response?.data;
    const detail = Array.isArray(payload?.errors)
      ? payload.errors.slice(0, 4).join(' · ')
      : '';
    if (detail) return `${payload?.error || 'שגיאה'}: ${detail}`;
    return payload?.error || 'שגיאה בתצוגה מקדימה של הקובץ';
  };

  const previewExcelFile = async (file) => {
    if (!file) {
      clearExcelPreview();
      return;
    }
    const reqId = ++previewReqRef.current;
    setPreviewLoading(true);
    setPreviewError('');
    setExcelPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/american-exams/import/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (reqId !== previewReqRef.current) return;
      setExcelPreview({
        questions: data.questions || [],
        counts: data.counts || {},
        imported: data.imported || 0,
      });
    } catch (err) {
      if (reqId !== previewReqRef.current) return;
      setExcelPreview(null);
      setPreviewError(formatPreviewApiError(err));
    } finally {
      if (reqId === previewReqRef.current) setPreviewLoading(false);
    }
  };

  const resetDialogState = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setFormError('');
    clearExcelPreview();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const closeDialog = () => {
    if (saving) return;
    resetDialogState();
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm(), frame });
    setFormError('');
    clearExcelPreview();
    setDialogOpen(true);
  };

  const resetReplaceState = () => {
    setReplaceTarget(null);
    setReplaceFile(null);
    setReplaceError('');
    clearExcelPreview();
    if (replaceFileRef.current) replaceFileRef.current.value = '';
  };

  const closeReplace = () => {
    if (replacing) return;
    resetReplaceState();
  };

  const openEdit = (exam) => {
    const m = exam.material || {};
    setEditingId(exam._id);
    setForm({
      title: exam.title || '',
      frame: exam.frame || 'daf_yomi',
      description: exam.description || '',
      tractate: m.tractate || null,
      tractateTo: m.tractateTo || m.tractate || null,
      dafFrom: m.dafFrom ?? null,
      dafTo: m.dafTo ?? null,
      amudFrom: m.amudFrom || 'a',
      amudTo: m.amudTo || 'b',
      file: null,
    });
    setFormError('');
    clearExcelPreview();
    setDialogOpen(true);
  };

  const buildMaterialPayload = () => {
    const tractateTo = form.tractateTo || form.tractate;
    return {
      tractate: form.tractate,
      tractateTo: tractateTo === form.tractate ? '' : tractateTo,
      dafFrom: Number(form.dafFrom),
      dafTo: Number(form.dafTo),
      amudFrom: form.frame === 'shinun' ? form.amudFrom : '',
      amudTo: form.frame === 'shinun' ? form.amudTo : '',
    };
  };

  const validateSettingsForm = ({ requireFile }) => {
    if (!form.frame) return 'יש לבחור סוג מבחן';
    if (!form.tractate) return 'יש לבחור מסכת התחלה';
    if (!(form.tractateTo || form.tractate)) return 'יש לבחור מסכת סיום';
    const dafFrom = Number(form.dafFrom);
    const dafTo = Number(form.dafTo);
    if (!Number.isInteger(dafFrom) || !Number.isInteger(dafTo) || dafFrom < 2 || dafTo < 2) {
      return 'יש לבחור דף מ ודף עד מהרשימה (לא רק להקליד — לבחור את השורה)';
    }
    if (form.frame === 'shinun' && (!form.amudFrom || !form.amudTo)) {
      return 'יש לבחור עמוד התחלה ועמוד סיום';
    }
    if (requireFile && !form.file) return 'יש להעלות קובץ אקסל';
    return '';
  };

  const handleSave = async () => {
    setFormError('');
    const isEdit = Boolean(editingId);
    const errMsg = validateSettingsForm({ requireFile: !isEdit });
    if (errMsg) {
      setFormError(errMsg);
      return;
    }
    if (!isEdit && !excelPreview?.questions?.length) {
      setFormError(previewError || 'יש לבחור קובץ אקסל תקין');
      return;
    }

    setSaving(true);
    let createdId = null;
    try {
      const material = buildMaterialPayload();
      if (isEdit) {
        await api.put(`/american-exams/${editingId}`, {
          title: form.title.trim(),
          frame: form.frame,
          description: form.description || '',
          material,
        });
        resetDialogState();
        setFlash({ severity: 'success', message: 'ההגדרות עודכנו' });
        load();
        return;
      }

      const { data: created } = await api.post('/american-exams', {
        title: form.title.trim(),
        frame: form.frame,
        description: form.description || '',
        material,
        sectionKeys: ['main'],
      });
      createdId = created._id || created.id;

      const imported = await importFile(createdId, form.file);
      const exam = imported.exam || created;

      resetDialogState();
      setFlash({
        severity: 'success',
        message: `המבחן נוצר — ${imported.imported} שאלות`,
      });
      load();
      goToEditor(exam._id || createdId);
    } catch (err) {
      if (createdId) {
        try {
          await api.delete(`/american-exams/${createdId}`);
        } catch {
          /* rollback best-effort */
        }
      }
      const payload = err.response?.data;
      const detail = Array.isArray(payload?.errors)
        ? payload.errors.slice(0, 4).join(' · ')
        : '';
      setFormError(
        detail
          ? `${payload?.error || 'שגיאה'}: ${detail}`
          : (payload?.error || (isEdit ? 'שגיאה בעדכון המבחן' : 'שגיאה ביצירת המבחן'))
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (exam) => {
    const next = exam.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/american-exams/${exam._id}`, { status: next });
      load();
    } catch (err) {
      setFlash({
        severity: 'error',
        message: err.response?.data?.error || 'שגיאה בעדכון סטטוס',
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/american-exams/${deleteTarget._id}`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setFlash({
        severity: 'error',
        message: err.response?.data?.error || 'שגיאה במחיקה',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleReplace = async () => {
    setReplaceError('');
    if (!replaceTarget || !replaceFile) {
      setReplaceError('יש לבחור קובץ אקסל');
      return;
    }
    if (!excelPreview?.questions?.length) {
      setReplaceError(previewError || 'יש לבחור קובץ אקסל תקין');
      return;
    }
    setReplacing(true);
    try {
      const imported = await importFile(replaceTarget._id, replaceFile);
      const examId = imported.exam?._id || replaceTarget._id;
      setFlash({
        severity: 'success',
        message: `הוחלף — ${imported.imported} שאלות`,
      });
      resetReplaceState();
      load();
      goToEditor(examId);
    } catch (err) {
      const payload = err.response?.data;
      const detail = Array.isArray(payload?.errors)
        ? payload.errors.slice(0, 3).join(' · ')
        : '';
      setReplaceError(detail || payload?.error || 'שגיאה בהחלפת הקובץ');
    } finally {
      setReplacing(false);
    }
  };

  const previewRows = useMemo(() => {
    const qs = excelPreview?.questions || [];
    return [...qs].sort((a, b) => {
      const sa = SECTION_ORDER.indexOf(a.sectionKey);
      const sb = SECTION_ORDER.indexOf(b.sectionKey);
      if (sa !== sb) return sa - sb;
      return (a.number || 0) - (b.number || 0);
    });
  }, [excelPreview]);

  const previewSummary = useMemo(
    () => formatCountsSummary(excelPreview?.questions || []),
    [excelPreview]
  );

  const createSaveDisabled =
    saving
    || (!editingId && (
      !form.file
      || previewLoading
      || Boolean(previewError)
      || !excelPreview?.questions?.length
    ));

  const replaceSaveDisabled =
    replacing
    || !replaceFile
    || previewLoading
    || Boolean(previewError)
    || !excelPreview?.questions?.length;

  const columns = useMemo(() => [
    {
      key: 'title',
      title: 'כותרת',
      sortable: true,
      filterable: true,
      minWidth: '160px',
    },
    {
      key: 'materialLabel',
      title: 'חומר',
      sortable: true,
      filterable: true,
      minWidth: '180px',
    },
    {
      key: 'questionCount',
      title: 'שאלות',
      sortable: true,
      filterable: false,
      minWidth: '70px',
      render: (v, row) => (
        <span title={row.countsSummary || ''}>{v || '—'}</span>
      ),
    },
    {
      key: 'status',
      title: 'סטטוס',
      sortable: true,
      filterable: true,
      minWidth: '80px',
      render: (v) => (v === 'active' ? 'פעיל' : 'כבוי'),
    },
    {
      key: 'ivrSlotLabel',
      title: 'בטלפון',
      sortable: true,
      filterable: true,
      minWidth: '130px',
      render: (v, row) => (
        <span title={row.createdAtLabel ? `נוצר: ${row.createdAtLabel}` : ''}>
          {v || '—'}
        </span>
      ),
    },
    {
      key: 'createdAtLabel',
      title: 'נוצר',
      sortable: true,
      filterable: false,
      minWidth: '140px',
    },
    {
      key: 'actions',
      title: 'פעולות',
      sortable: false,
      filterable: false,
      minWidth: '230px',
      render: (_v, row) => (
        <RowActionsMenu
          row={row}
          onEditExam={(exam) => goToEditor(exam._id)}
          onDownloadWord={downloadWord}
          onEditDetails={openEdit}
          onReplaceExcel={(exam) => {
            clearExcelPreview();
            setReplaceTarget(exam);
            setReplaceFile(null);
            setReplaceError('');
          }}
          onToggleActive={toggleActive}
          onDelete={setDeleteTarget}
        />
      ),
    },
  ], [goToEditor]);

  const tableData = useMemo(() => {
    const ivrSlots = buildIvrSlotMap(exams);
    return exams.map((exam) => {
      const slot = ivrSlots[exam._id];
      return {
        ...exam,
        materialLabel: formatMaterial(exam.material, exam.frame) || '—',
        questionCount: questionTotal(exam),
        countsSummary: formatCountsSummary(exam.questions || []),
        ivrSlotLabel: slot ? IVR_SLOT_LABELS[slot] : '—',
        createdAtLabel: formatCreatedAt(exam.createdAt),
      };
    });
  }, [exams]);

  if (!ALLOWED_FRAMES.has(frame)) {
    return <Navigate to="/american-exams/daf_yomi" replace />;
  }

  const frameLabel = FRAME_LABELS[frame] || frame;

  return (
    <div>
      <AdminPageHeader
        title={`מבחנים אמריקאים — ${frameLabel}`}
        sub="מסכת + טווח דפים + אקסל. הנבחן עונה לפי הדף המודפס (1–3)."
        primary={{ label: 'מבחן חדש', onClick: openCreate }}
        legacy={(
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>
                מבחנים אמריקאים — {frameLabel}
              </h1>
              <p style={{ margin: '4px 0 0', color: 'var(--ck-text-2, #64748b)', fontSize: '0.9rem' }}>
                מסכת + טווח דפים + אקסל. הנבחן עונה לפי הדף המודפס (1–3).
              </p>
            </div>
            <Button size="small" variant="contained" color="secondary" onClick={openCreate}>
              + מבחן חדש
            </Button>
          </div>
        )}
      />

      {flash && (
        <div style={{ marginBottom: 12 }}>
          <FormAlert severity={flash.severity}>{flash.message}</FormAlert>
        </div>
      )}

      <TableKit
        columns={columns}
        data={tableData}
        total={tableData.length}
        loading={loading}
        clientSideMode
        emptyMessage={`לא נמצאו מבחנים אמריקאים ל«${frameLabel}»`}
        exportFileName={`מבחנים_אמריקאים_${frame}`}
      />

      {/* יצירה / עריכה */}
      <FormDialog open={dialogOpen} onClose={closeDialog} size={editingId ? 'md' : 'lg'}>
        <FormCard
          title={editingId ? 'עריכת מבחן אמריקאי' : 'מבחן אמריקאי חדש'}
          info={
            editingId
              ? 'עדכון כותרת, סוג, חומר ותיאור. החלפת אקסל נעשית בנפרד.'
              : 'בחרו טווח חומר, והעלו קובץ אקסל.'
          }
          error={formError || (!editingId ? previewError : '')}
          onSave={handleSave}
          onCancel={closeDialog}
          saveLabel={
            saving
              ? (editingId ? 'שומר...' : 'יוצר ומייבא...')
              : (editingId ? 'שמירה' : 'יצירה וייבוא')
          }
          saving={saving}
          saveDisabled={createSaveDisabled}
        >
          <FormSection title="פרטי מבחן">
            <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--ck-text-2, #64748b)' }}>
              סוג: <strong>{FRAME_LABELS[form.frame] || form.frame}</strong>
            </div>
            <FormGrid cols={1}>
              <FormField
                label="כותרת (אופציונלי)"
                value={form.title}
                onChange={(v) => setForm((f) => ({ ...f, title: v }))}
                placeholder="אם ריק — לפי מסכת ודפים"
              />
            </FormGrid>
          </FormSection>

          <FormSection title="טווח חומר">
            <FormGrid cols={2}>
              <SearchSelect
                label="מסכת מ"
                required
                options={TRACTATE_NAMES}
                value={form.tractate}
                onChange={handleTractateFromChange}
                placeholder="הקלד לחיפוש מסכת..."
              />
              <DafAmudField
                label="דף מ"
                required
                dafOptions={dafFromOptions}
                dafValue={form.dafFrom}
                onDafChange={(v) => setForm((f) => ({ ...f, dafFrom: v }))}
                dafPlaceholder={form.tractate ? 'הקלד לחיפוש דף...' : 'בחרו מסכת קודם'}
                showAmud={form.frame === 'shinun'}
                amudValue={form.amudFrom}
                onAmudChange={(v) => setForm((f) => ({ ...f, amudFrom: v }))}
              />
              <SearchSelect
                label="מסכת עד"
                required
                options={TRACTATE_NAMES}
                value={form.tractateTo || form.tractate}
                onChange={handleTractateToChange}
                placeholder="הקלד לחיפוש מסכת..."
              />
              <DafAmudField
                label="דף עד"
                required
                dafOptions={dafToOptions}
                dafValue={form.dafTo}
                onDafChange={(v) => setForm((f) => ({ ...f, dafTo: v }))}
                dafPlaceholder={(form.tractateTo || form.tractate) ? 'הקלד לחיפוש דף...' : 'בחרו מסכת קודם'}
                showAmud={form.frame === 'shinun'}
                amudValue={form.amudTo}
                onAmudChange={(v) => setForm((f) => ({ ...f, amudTo: v }))}
              />
            </FormGrid>
          </FormSection>

          <FormSection>
            <FormGrid cols={1}>
              <FormField
                label="תיאור (אופציונלי)"
                value={form.description}
                onChange={(v) => setForm((f) => ({ ...f, description: v }))}
              />
            </FormGrid>
          </FormSection>

          {!editingId && (
            <FormSection title="קובץ אקסל">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                <button
                  type="button"
                  className="formkit-btn formkit-btn--outline"
                  onClick={handleDownloadTemplate}
                  disabled={downloadingTemplate}
                >
                  {downloadingTemplate ? 'מוריד...' : 'הורד תבנית'}
                </button>
                <span style={{ fontSize: 13, color: 'var(--ck-text-2, #64748b)' }}>
                  מספר_שאלה · סוג_שאלה · טקסט_שאלה · אפשרות_1–3 · תשובה_נכונה
                </span>
              </div>
              <div className="formkit-field">
                <span className="formkit-label">קובץ אקסל *</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <button
                    type="button"
                    className="formkit-btn formkit-btn--outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    בחר קובץ
                  </button>
                  <span style={{ fontSize: 13, color: 'var(--ck-text-2, #64748b)' }}>
                    {form.file ? form.file.name : 'לא נבחר קובץ'}
                    {previewLoading ? ' · טוען תצוגה מקדימה...' : ''}
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setForm((f) => ({ ...f, file }));
                      previewExcelFile(file);
                    }}
                  />
                </div>
              </div>
              {excelPreview?.questions?.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 13, color: 'var(--ck-text-2, #64748b)', marginBottom: 8 }}>
                    תצוגה מקדימה · {excelPreview.imported} שאלות
                    {previewSummary ? ` · ${previewSummary}` : ''}
                  </div>
                  <SimpleTable
                    columns={MAPPING_COLUMNS}
                    rows={previewRows}
                    emptyMsg="אין שאלות"
                    maxHeight={320}
                  />
                </div>
              )}
            </FormSection>
          )}
        </FormCard>
      </FormDialog>

      {/* החלפת אקסל */}
      <FormDialog
        open={Boolean(replaceTarget)}
        onClose={closeReplace}
        size="lg"
      >
        <FormCard
          title={replaceTarget ? `החלף אקסל · ${replaceTarget.title}` : 'החלף אקסל'}
          error={replaceError || previewError}
          onSave={handleReplace}
          onCancel={closeReplace}
          saveLabel={replacing ? 'מייבא...' : 'ייבוא'}
          saving={replacing}
          saveDisabled={replaceSaveDisabled}
        >
          <FormSection>
            <div className="formkit-field">
              <span className="formkit-label">קובץ אקסל חדש *</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  className="formkit-btn formkit-btn--outline"
                  onClick={() => replaceFileRef.current?.click()}
                >
                  בחר קובץ
                </button>
                <span style={{ fontSize: 13, color: 'var(--ck-text-2, #64748b)' }}>
                  {replaceFile ? replaceFile.name : 'לא נבחר קובץ'}
                  {previewLoading ? ' · טוען תצוגה מקדימה...' : ''}
                </span>
                <input
                  ref={replaceFileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setReplaceFile(file);
                    previewExcelFile(file);
                  }}
                />
              </div>
            </div>
            {excelPreview?.questions?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, color: 'var(--ck-text-2, #64748b)', marginBottom: 8 }}>
                  תצוגה מקדימה · {excelPreview.imported} שאלות
                  {previewSummary ? ` · ${previewSummary}` : ''}
                </div>
                <SimpleTable
                  columns={MAPPING_COLUMNS}
                  rows={previewRows}
                  emptyMsg="אין שאלות"
                  maxHeight={320}
                />
              </div>
            )}
          </FormSection>
        </FormCard>
      </FormDialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="מחיקת מבחן"
        message={deleteTarget ? `למחוק את «${deleteTarget.title}»?` : ''}
        confirmText="מחק"
        confirmColor="error"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => !deleting && setDeleteTarget(null)}
      />
    </div>
  );
};

export default AmericanExams;
